import React, { PropsWithChildren, useEffect } from 'react';
import {
  useRecoilState,
  useRecoilValue,
  useResetRecoilState,
  useSetRecoilState
} from 'recoil';
import { authState } from '@/recoil/auth';
import { pharmacyState } from '@/recoil/pharmacy';
import usePharmacyQuery from '@/hooks/pharmacy/usePharmacyQuery';
import { checkDomain } from '@/utils/domain';
import { getCookie } from '@/utils/cookie';
import { serviceTokenMap } from '@/constants/cookie';
import GlobalAlert from '@/components/GlobalAlert';
import { datadogLogs } from '@datadog/browser-logs';
import _ from 'lodash';
import useFirebaseListener from '@/hooks/common/useFirebaseListener';
import useChannelTalk from '@/utils/channelTalkService';

const AuthGuard = (props: PropsWithChildren<any>) => {
  const { children } = props;

  const [auth, setAuth] = useRecoilState(authState);
  const resetAuth = useResetRecoilState(authState);

  const setPharmacy = useSetRecoilState(pharmacyState);
  const resetPharmacy = useResetRecoilState(pharmacyState);

  const { pharmacyDetailQuery } = usePharmacyQuery();

  useEffect(() => {
    const serviceType = checkDomain();
    const tokenFromCookie = getCookie(serviceTokenMap[serviceType]);
    if (tokenFromCookie) {
      resetPharmacy();
      setAuth((prevAuth: any) => ({
        ...prevAuth,
        token: tokenFromCookie
      }));
      return;
    }
  }, []);

  const { data: res } = pharmacyDetailQuery();

  useEffect(() => {
    if (!res?.getPharmacy) {
      return;
    }

    const pharmacyInfo = res.getPharmacy;

    if (pharmacyInfo.partnerShip !== 'partnership') {
      resetAuth();
      resetPharmacy();
      return GlobalAlert.show({
        title: '파트너 제휴',
        body: '약국 제휴를 위해 닥터나우 고객센터 또는 \n담당 매니저에게 연락바랍니다.'
      });
    }

    setPharmacy({
      ...res.getPharmacy.pharmacist,
      pharmacy: {
        ...res.getPharmacy
      },
      account: { ...res.getPharmacy.accountInfo },
      bankAccount: {
        ...res.getPharmacy.pharmacist,
        account: res.getPharmacy.pharmacist?.accountNumber
      },
      pharmacyDuty: res.getPharmacy.pharmacyDuties,
      flag: 'pharmacy'
    });

    // 약국 정보가 set 될 때 로거에 약국 정보 셋팅
    datadogLogs.logger.setContext({
      pharmacy: {
        pharmacyId: res.getPharmacy.id,
        pharmacyName: res.getPharmacy.name
      }
    });
  }, [res]);

  const pharmacy = useRecoilValue(pharmacyState);

  const isLoggedIn = !_.isEmpty(pharmacy) && !!auth.token;
  useFirebaseListener();

  useEffect(() => {
    if (!_.isEmpty(pharmacy)) {
      isLoggedIn
        ? useChannelTalk(pharmacy).boot()
        : useChannelTalk(pharmacy).shutdown();
    }
  }, [isLoggedIn]);

  return <>{children}</>;
};

export default AuthGuard;
